'use client';

import TableAG from '@/components/custom-table-ag';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSidebarStore } from '@/hooks/use-sidebar-store';
import { cn } from '@/lib/utils';
import { ChevronRight, Loader2, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Dispatch, memo, SetStateAction, useCallback } from 'react';

function SidebarSearch({
    className,
    keyword,
    setKeyword,
}: {
    className?: string;
    keyword: string;
    setKeyword: Dispatch<SetStateAction<string>>;
}) {
    const transSidebar = useTranslations('Sidebar');

    const isLoading = useSidebarStore((state) => state.isLoading);
    const isShowSidebarIconOnly = useSidebarStore(
        (state) => state.isShowSidebarIconOnly
    );
    const isShowMenuOverlay = useSidebarStore(
        (state) => state.isShowMenuOverlay
    );
    const setIsShowSidebarIconOnly = useSidebarStore(
        (state) => state.setIsShowSidebarIconOnly
    );

    const { triggerSizeColumnToFit } = TableAG.useTableAG();

    const onChangeSidebarSize = useCallback(() => {
        setIsShowSidebarIconOnly((x) => !x);
        setTimeout(() => {
            triggerSizeColumnToFit?.();
        }, 250);
    }, [setIsShowSidebarIconOnly, triggerSizeColumnToFit]);

    return (
        <div className={cn('flex h-11 w-full items-center', className)}>
            <div
                className={cn(
                    !isShowMenuOverlay && isShowSidebarIconOnly
                        ? 'hidden'
                        : 'relative w-full pl-2'
                )}
            >
                <Input
                    placeholder={
                        transSidebar('Sidebar_SearchSidebarText') + '...'
                    }
                    className='h-9 w-full rounded-r-none pr-9 ring-0 focus-visible:ring-0 focus-visible:ring-transparent'
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    autoFocus={false}
                />
                {isLoading ? (
                    <div
                        aria-hidden
                        className='absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center'
                    >
                        <Loader2 size={16} className='animate-spin' />
                    </div>
                ) : (
                    <div>
                        {keyword.length > 0 ? (
                            <X
                                onClick={() => setKeyword('')}
                                size={16}
                                className='absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer hover:scale-105'
                            />
                        ) : null}
                    </div>
                )}
            </div>
            <Button
                variant={'secondary'}
                size='icon'
                className={cn('hidden h-9 grow rounded-r-full sm:flex')}
                onClick={onChangeSidebarSize}
            >
                <ChevronRight
                    strokeWidth={3}
                    className={cn(
                        'h-5 w-5 transition-transform duration-300 ease-in-out',
                        {
                            'relative -translate-x-0.5 rotate-180':
                                !isShowSidebarIconOnly,
                        }
                    )}
                />

                <span className='sr-only'>Toggle sidebar icon</span>
            </Button>
        </div>
    );
}
export default memo(SidebarSearch);
